/**
 * @file Reconcile (`#/reconcile`) — admin only.
 *
 * The "Abra is wrong, fix it" queue. Surfaces every device where Gear's
 * cross-system join disagrees with Abra Flexi, presented one-at-a-time
 * so an admin can walk through the list: see what Meraki / the testing
 * sheet thinks, open the record in Abra, fix it, click Next, repeat.
 *
 * Reconcile is intentionally separate from `/devices` and `/data-quality`:
 *   • `/devices` is the global inventory view (browse + filter).
 *   • `/data-quality` is Gear-side device-state issues (unassigned, in
 *     storage, orphaned by employee-number).
 *   • `/reconcile` is "the source of truth has drifted and a human has
 *     to go fix Abra" — a different workflow that benefits from a
 *     focused queue.
 *
 * Flags surfaced (same set as the Devices "Flags" filter):
 *
 *   alert  testing_not_in_abra   — Testing sheet has it, Abra doesn't
 *   alert  orphan_no_abra        — Meraki has it, Abra doesn't
 *   warn   holder_mismatch       — SN matches Abra+Meraki but holders differ
 *   warn   testing_wrong_holder  — Abra holder ≠ testing sheet's USED BY
 *
 * Gear-only peripherals (mice, keyboards, etc.) are NOT in this queue.
 * They're a person-parameter, not inventory — they live on `/me` and
 * `/people/:id` and never end up in Abra.
 *
 * The "fix" is always done in Abra (or Meraki, or the testing sheet —
 * whichever has the wrong data). After fixing, the admin clicks "Sync
 * everything" on Settings (or waits for the nightly cron). Rows that
 * were genuinely fixed disappear; rows still wrong reappear.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { Icons, categoryIcon }   from '../icons.js';
import {
  CATEGORIES, flexiWebUrl,
  TESTING_SHEET_URL, MERAKI_DASHBOARD_URL,
} from '../config.js';
import { watchDevices, watchPendingChanges } from '../db.js';
import { promptRegisterHandover } from '../lib/register-prompt.js';
import { setPersonalDevice, submitGearAction } from '../api.js';
import { getEmployee } from '../db.js';
import { normaliseSnForMatch } from '../lib/sn-match.js';
import { renderPageHead } from '../page-header.js';
import { navigate }       from '../router.js';
import { copyable }       from '../lib/copyable.js';

// One entry per flag type. Order in the array drives display order in
// the filter chips. `severity` drives the colour. `pick(d)` returns the
// flag-specific row data we render in the right pane.
const FLAG_KINDS = [
  {
    id:       'meraki_orphan',
    severity: 'alert',
    label:    'Not in Abra (Meraki)',
    summary:  'In Meraki, not in Abra.',
    pick: (d) => d.match?.status === 'orphan_no_abra' && d.source === 'meraki',
  },
  {
    id:       'iru_orphan',
    severity: 'alert',
    label:    'Not in Abra (IRU)',
    summary:  'In IRU, not in Abra.',
    pick: (d) => d.match?.status === 'orphan_no_abra' && d.source === 'iru',
  },
  {
    id:       'testing_orphan',
    severity: 'alert',
    label:    'Not in Abra (Testing)',
    summary:  'In the testing sheet, not in Abra (no SN or IMEI match).',
    pick: (d) => d.match?.status === 'testing_not_in_abra',
  },
  {
    id:       'meraki_holder',
    severity: 'warn',
    label:    'MDM holder mismatch',
    summary:  'Abra and the MDM disagree on who has it.',
    pick: (d) => d.match?.status === 'holder_mismatch',
  },
  {
    id:       'testing_holder',
    severity: 'warn',
    label:    'Testing holder mismatch',
    summary:  'The testing-devices sheet says one person has it; Abra says another. Update the Abra stitky tag to match the sheet, or fix the sheet if the sheet is the one out of date.',
    pick: (d) => d.match?.testing?.status === 'wrong_holder',
  },
];

const SEVERITY_LABELS = {
  alert: 'Alert',
  warn:  'Warn',
};

export function renderReconcile(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Reconcile',
    "Abra is the source of truth. This queue surfaces every device where it's drifted from reality — orphans Meraki or the QA sheet know about, plus holder mismatches. Click an item, open it in Abra (or Meraki / the sheet — whichever's wrong), fix it there, then sync. Fixed items disappear on the next sync. Gear-only peripherals like keyboards aren't in this list — they're not meant to be in Abra.",
  ));

  // ── State ──────────────────────────────────────────────────────────────
  const state = {
    severity: 'all',  // 'all' | 'alert' | 'warn'
    kind:     'all',  // 'all' | one of FLAG_KINDS[].id
    devices:  [],
    selectedId: null,
  };

  // Orphans already handed over via `register` and waiting for Finance —
  // keyed by device id and by normalised serial (a register row may predate
  // the orphan doc, or come from the person page with no device id).
  let pendingIds = new Set();
  let pendingSns = new Set();
  let pendingAnyIds = new Set(); // any open action (transfer, take-back…) on an Abra device
  const isPending = (d) => (d.flexiKod
    ? pendingAnyIds.has(d.id)
    : pendingIds.has(d.id) || pendingSns.has(normaliseSnForMatch(d.sn)));

  // ── Filter bar ─────────────────────────────────────────────────────────
  const severityBar = el('div', { class: 'segment' });
  for (const s of ['all', 'alert', 'warn']) {
    const btn = el('button', { class: state.severity === s ? 'active' : null },
      s === 'all' ? 'All' : SEVERITY_LABELS[s]);
    btn.addEventListener('click', () => {
      state.severity = s;
      for (const b of severityBar.children) b.classList.toggle('active', b === btn);
      rerender();
    });
    severityBar.appendChild(btn);
  }

  const kindBar = el('div', { class: 'segment' });
  const allKindBtn = el('button', { class: 'active' }, 'All kinds');
  allKindBtn.addEventListener('click', () => {
    state.kind = 'all';
    for (const b of kindBar.children) b.classList.toggle('active', b === allKindBtn);
    rerender();
  });
  kindBar.appendChild(allKindBtn);
  for (const f of FLAG_KINDS) {
    const btn = el('button', null, f.label);
    btn.addEventListener('click', () => {
      state.kind = f.id;
      for (const b of kindBar.children) b.classList.toggle('active', b === btn);
      rerender();
    });
    kindBar.appendChild(btn);
  }

  host.appendChild(el('div', { class: 'toolbar', style: 'flex-wrap:wrap;gap:8px' },
    severityBar, kindBar,
  ));

  // ── Layout: rail + detail ──────────────────────────────────────────────
  const grid = el('div', { class: 'detail-grid' });
  host.appendChild(grid);
  const railHost   = el('aside', { class: 'detail-side' });
  const detailHost = el('div');
  grid.appendChild(railHost);
  grid.appendChild(detailHost);

  // Per-row DOM nodes by deviceId, so selection changes can toggle the
  // active class WITHOUT rebuilding the rail (which would nuke scroll
  // position). Rebuilt only when the data changes or filters change.
  let railRowsById = new Map();

  // ── Live data ──────────────────────────────────────────────────────────
  // Same global /devices stream the Devices index uses. Reconcile filters
  // client-side — the data we need is all on the materialised match block,
  // no extra reads.
  // Rail order as last rendered — when the selected device drops off the
  // queue (marked personal, fixed by a sync), the selection moves to the
  // row that was BELOW it, not back to the top.
  let lastVisibleIds = [];
  const unsub = watchDevices({}, (devices) => {
    state.devices = devices.filter((d) => flagKindFor(d));
    if (!state.selectedId || !state.devices.find((d) => d.id === state.selectedId)) {
      const visible = applyFilters(state.devices, state);
      const stillThere = new Set(visible.map((d) => d.id));
      const at = lastVisibleIds.indexOf(state.selectedId);
      const next = at < 0 ? null
        : (lastVisibleIds.slice(at + 1).find((id) => stillThere.has(id))
          || lastVisibleIds.slice(0, at).reverse().find((id) => stillThere.has(id)));
      state.selectedId = next || visible[0]?.id || null;
    }
    rebuildRail();
    updateDetail();
  });
  host.addEventListener('cleanup', unsub, { once: true });

  const unsubPending = watchPendingChanges({ status: 'pending' }, (rows) => {
    pendingAnyIds = new Set(rows.map((r) => r.deviceId).filter(Boolean));
    const regs = rows.filter((r) => r.action === 'register');
    pendingIds = new Set(regs.map((r) => r.deviceId).filter(Boolean));
    pendingSns = new Set(regs.map((r) => r.snKey || normaliseSnForMatch(r.device?.sn)).filter(Boolean));
    // In place: no rail rebuild, so the scroll position survives a hand-over.
    for (const [id, node] of railRowsById) {
      const d = state.devices.find((x) => x.id === id);
      const sub = node.querySelector('.reconcile-row-sub');
      if (d && sub) sub.textContent = railSub(d, isPending);
    }
    updateDetail();
  });
  host.addEventListener('cleanup', unsubPending, { once: true });

  function rebuildRail() {
    const visible = applyFilters(state.devices, state);
    lastVisibleIds = visible.map((d) => d.id);
    // Keep the rail's own scroll position across rebuilds (every /devices
    // snapshot rebuilds it), then make sure the selected row is in view.
    const scrollTop = railHost.firstElementChild?.scrollTop || 0;
    clear(railHost);
    railRowsById = new Map();
    railHost.appendChild(renderRail(visible, state, railRowsById, isPending, (id) => {
      state.selectedId = id;
      // No rebuild — just toggle the `active` class on the relevant rows.
      // Scroll position stays put.
      for (const [otherId, node] of railRowsById) {
        node.classList.toggle('active', otherId === id);
      }
      updateDetail();
    }));
    const rail = railHost.firstElementChild;
    if (rail) rail.scrollTop = scrollTop;
    // Bring the selected row into the rail's view — scrolling the rail only,
    // never the page (scrollIntoView would move the window too).
    const row = railRowsById.get(state.selectedId);
    if (rail && row) {
      const r = row.getBoundingClientRect(); const c = rail.getBoundingClientRect();
      if (r.top < c.top) rail.scrollTop -= c.top - r.top;
      else if (r.bottom > c.bottom) rail.scrollTop += r.bottom - c.bottom;
    }
  }

  function updateDetail() {
    const visible = applyFilters(state.devices, state);
    clear(detailHost);
    const focused = visible.find((d) => d.id === state.selectedId) || visible[0] || null;
    detailHost.appendChild(renderDetail(focused, state.devices.length, visible.length, isPending));
  }

  // For filter clicks — they DO need to rebuild the rail (visible set
  // changes). Used by the closures in the filter bar above.
  function rerender() {
    rebuildRail();
    updateDetail();
  }
}

// ── Filter ────────────────────────────────────────────────────────────────
function applyFilters(devices, state) {
  return devices.filter((d) => {
    const kind = flagKindFor(d);
    if (!kind) return false;
    if (state.severity !== 'all' && kind.severity !== state.severity) return false;
    if (state.kind     !== 'all' && kind.id        !== state.kind)     return false;
    return true;
  });
}

/** The strongest applicable flag for a device. Order in FLAG_KINDS matters:
 *  testing_not_in_abra outranks holder mismatches because the alert level
 *  drives the rail's severity dot. */
function flagKindFor(d) {
  for (const f of FLAG_KINDS) {
    if (f.pick(d)) return f;
  }
  return null;
}

// ── Left rail ─────────────────────────────────────────────────────────────
//
// The rail's scrollbar sits on the LEFT edge via the `direction: rtl`
// trick (with children flipped back to `ltr`). That keeps the right
// edge — the one adjacent to the detail card — unbroken by a scroll
// track, so the active row's right-side chevron points cleanly at the
// card.
//
// `rowsById` is filled in here; the parent uses it later to toggle
// `.active` without rebuilding the list.
function railSub(d, isPending) {
  return `${flagKindFor(d).label}${isPending(d) ? ' · hand-over pending' : ''}`;
}

function renderRail(visible, state, rowsById, isPending, onPick) {
  const card = el('div', {
    class: 'card',
    style: 'padding:0;max-height:70vh;overflow:auto;direction:rtl',
  });
  if (!visible.length) {
    card.appendChild(el('div', { class: 'empty', style: 'padding:32px 16px;direction:ltr' },
      Icons.inbox(),
      el('p', null, state.devices.length ? 'No items match the current filters.' : 'Everything reconciles cleanly.'),
      el('p', { class: 'muted' }, state.devices.length
        ? 'Loosen the severity or kind filters.'
        : "Run \"Sync everything\" if you've just fixed something in Abra."),
    ));
    return card;
  }
  const list = el('div', { class: 'stack', style: 'gap:0;direction:ltr' });
  for (const d of visible) {
    const kind = flagKindFor(d);
    const active = d.id === state.selectedId;
    const dot = el('span', {
      class: 'reconcile-dot',
      title: kind.label,
      style: `width:8px;height:8px;display:inline-block;flex-shrink:0;background:${kind.severity === 'alert' ? 'var(--orange)' : 'var(--status-progress)'}`,
    });
    const chevron = el('span', {
      class: 'reconcile-chevron',
      'aria-hidden': 'true',
      style: 'margin-left:auto;color:var(--red);opacity:0;transition:opacity 120ms',
    }, '▸');
    const row = el('button', {
      class: `reconcile-row${active ? ' active' : ''}`,
      onClick: () => onPick(d.id),
    },
      dot,
      el('div', { class: 'reconcile-row-body' },
        el('div', { class: 'reconcile-row-title' },
          d.name ? copyable(d.name, { label: 'name' }) : (d.testing?.model || d.meraki?.systemModel || '(unnamed)')),
        el('div', { class: 'reconcile-row-sub muted' }, railSub(d, isPending)),
      ),
      chevron,
    );
    rowsById.set(d.id, row);
    list.appendChild(row);
  }
  card.appendChild(list);
  return card;
}

// ── Right pane (detail) ───────────────────────────────────────────────────
function renderDetail(device, totalFlagged, totalVisible, isPending) {
  const wrap = el('div', { class: 'stack' });

  if (!device) {
    wrap.appendChild(el('div', { class: 'card empty' },
      Icons.inbox(),
      el('p', null, totalFlagged ? 'Pick an item from the rail to start.' : 'Nothing to reconcile right now.'),
    ));
    return wrap;
  }

  const kind = flagKindFor(device);
  const cat  = CATEGORIES[device.category] || { label: device.category || 'device' };

  // ── Header card ──
  wrap.appendChild(el('section', { class: 'card' },
    el('div', { class: 'row', style: 'justify-content:space-between;align-items:flex-start;gap:12px' },
      el('div', null,
        el('div', { style: 'display:flex;gap:8px;align-items:center;font-size:18px;font-weight:700' },
          categoryIcon(device.category),
          device.name ? copyable(device.name, { label: 'name' }) : (device.testing?.model || device.meraki?.systemModel || '(unnamed device)'),
        ),
        el('div', { class: 'muted', style: 'margin-top:4px;font-size:var(--fs-small)' },
          [cat.label, device.testing?.model, device.meraki?.systemModel]
            .filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join(' · '),
        ),
      ),
      el('span', {
        class: `chip ${kind.severity === 'alert' ? 'chip-warning' : 'chip-mismatch'}`,
      }, kind.label),
    ),
    el('p', { class: 'muted', style: 'margin-top:14px' }, kind.summary),
    el('div', { class: 'row', style: 'gap:8px;margin-top:14px;flex-wrap:wrap' },
      ...renderActionButtons(device, kind, isPending),
    ),
    el('p', { class: 'muted', style: 'margin-top:14px;font-size:var(--fs-small)' },
      `Showing ${totalVisible} of ${totalFlagged} flagged. After fixing in Abra, click "Sync everything" on Settings to refresh — this row disappears if the fix worked.`),
  ));

  // ── What each system says ──
  wrap.appendChild(renderSideBySide(device, kind));

  // ── Open detail ──
  wrap.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'Device record'),
    el('p', { class: 'muted', style: 'margin:0 0 12px' },
      'Open the full device detail to see all fields, Gear notes, and history.'),
    el('button', {
      class: 'btn-outline btn',
      onClick: () => navigate(`/devices/${device.id}`),
    }, 'Open in Gear ↗'),
  ));

  return wrap;
}

function renderActionButtons(device, kind, isPending) {
  const buttons = [];
  // In Abra → deep-link to the record. Not in Abra → "Hand over to…": a
  // `register` gear action, so Finance gets the hand-over ping and creates
  // the asset (the Abra web view can't add records anyway).
  if (device.flexiKod) {
    // Holder mismatch: offer the transfer to whoever really has it.
    const transfer = kind.id === 'meraki_holder' ? renderTransferToMdmHolder(device, isPending) : null;
    if (transfer) buttons.push(transfer);
    buttons.push(el('a', {
      href:   flexiWebUrl(device.flexiKod),
      target: '_blank', rel: 'noopener noreferrer',
      class:  transfer ? 'btn-outline btn' : 'btn btn-lg',
    }, 'Open in Abra ↗'));
  } else if (isPending(device)) {
    buttons.push(el('button', { class: 'btn btn-lg', disabled: true }, 'Hand-over pending'));
  } else {
    buttons.push(el('button', {
      class: 'btn btn-lg',
      disabled: !device.sn,
      title: device.sn ? null : 'No serial number',
      onClick: () => promptRegisterHandover(device),
    }, 'Hand over to…'));
    // The person's own laptop, in the MDM for data security only. The row
    // leaves this queue as soon as the device doc updates.
    if (device.sn && (device.source === 'iru' || device.source === 'meraki')) {
      buttons.push(el('button', {
        class: 'btn-outline btn',
        onClick: async (e) => {
          const btn = e.currentTarget; // currentTarget is gone after the await
          if (!window.confirm(`Mark ${device.name || 'this device'} as personal?`)) return;
          btn.disabled = true;
          try {
            await setPersonalDevice({ deviceId: device.id, personal: true });
            toast('Marked personal.', 'success');
          } catch (err) {
            toast(err.message || 'Could not update the device.', 'error');
            btn.disabled = false;
          }
        },
      }, 'Mark as personal'));
    }
  }

  // Secondary: link to whichever non-Abra system flagged this. "MDM holder
  // mismatch" can be IRU-only, so only offer Meraki when Meraki has it.
  if (kind.id.startsWith('meraki') && device.meraki) {
    buttons.push(el('a', {
      href:   MERAKI_DASHBOARD_URL,
      target: '_blank', rel: 'noopener noreferrer',
      class:  'btn-outline btn',
      title:  device.meraki?.sn ? `Search Meraki for SN ${device.meraki.sn}` : 'Meraki dashboard',
    }, 'Open Meraki ↗'));
  }
  if (kind.id.startsWith('testing')) {
    buttons.push(el('a', {
      href:   TESTING_SHEET_URL,
      target: '_blank', rel: 'noopener noreferrer',
      class:  'btn-outline btn',
      title:  device.testing?.sheetTab ? `Sheet tab: ${device.testing.sheetTab}` : 'Testing devices sheet',
    }, 'Open testing sheet ↗'));
  }
  return buttons;
}

/**
 * Who really has the device, by precedence: IRU, then Meraki (Abra is the
 * one being corrected). An MDM with no owner doesn't count. Returns
 * { source, email, employeeId } — employeeId null when the MDM's email
 * isn't a Bamboo employee.
 */
function mdmTruth(device) {
  for (const [source, side] of [['IRU', device.iru], ['Meraki', device.meraki]]) {
    if (side?.ownerEmail) return { source, email: side.ownerEmail, employeeId: side.ownerEmployeeId || null };
  }
  return null;
}

// "Transfer to <person>" for a holder mismatch: a regular `transfer` gear
// action from the Abra holder to the MDM's holder, so Finance gets the
// usual ping and the row reconciles once Abra is updated.
function renderTransferToMdmHolder(device, isPending) {
  const truth = mdmTruth(device);
  const abraHolderId = device.abra?.holderEmployeeId || device.currentHolder || null;
  if (!truth) return null;
  if (truth.employeeId && truth.employeeId === abraHolderId) {
    return el('span', { class: 'muted', style: 'align-self:center' }, `${truth.source} matches Abra.`);
  }
  if (isPending(device)) return el('button', { class: 'btn btn-lg', disabled: true }, 'Transfer pending');
  if (!truth.employeeId) {
    return el('button', { class: 'btn btn-lg', disabled: true, title: `${truth.email} isn't in Bamboo` }, `Transfer to ${truth.email}`);
  }
  return el('button', {
    class: 'btn btn-lg',
    title: `${truth.source} says ${truth.email} has it`,
    onClick: async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      try {
        const [to, from] = await Promise.all([
          getEmployee(truth.employeeId),
          abraHolderId ? getEmployee(abraHolderId) : null,
        ]);
        const toName = to?.fullName || truth.email;
        if (!window.confirm(`Transfer ${device.name || 'this device'} to ${toName}?`)) { btn.disabled = false; return; }
        const res = await submitGearAction({
          action:   abraHolderId ? 'transfer' : 'handover',
          deviceId: device.id,
          payload: {
            toHolder:   { employeeId: truth.employeeId, name: toName, email: truth.email },
            fromHolder: abraHolderId ? { employeeId: abraHolderId, name: from?.fullName || device.abra?.holderEmail || null } : null,
          },
        });
        if (res?.slack?.ok) toast('Transfer submitted. Finance notified.', 'success');
        else toast(`Transfer submitted, but the Slack ping failed (${res?.slack?.error || res?.slack?.reason || 'unknown'}). Retrying automatically.`, 'error', 8000);
      } catch (err) {
        console.error('[reconcile] transfer failed', err);
        toast(err.message || 'Transfer failed.', 'error');
        btn.disabled = false;
      }
    },
  }, `Transfer to ${truth.email}`);
}

function renderSideBySide(device, kind) {
  const cells = [];
  cells.push(renderSourceCard('Abra (Flexi)', device.abra ? {
    'Flexi kod':  device.abra.flexiKod || '—',
    'SN':         device.abra.sn ? copyable(device.abra.sn, { label: 'SN', mono: true }) : '—',
    'Holder':     device.abra.holderEmail || (device.abra.holderUnresolved ? 'Orphan tag (no Bamboo match)' : 'Unassigned'),
    'Stitky':     device.abra.rawStitky || '—',
    'Poznám':     device.abra.rawPoznam || '—',
    'Category':   device.abra.name || device.abra.flexiKod || '—',
  } : null, device.abra ? 'present' : 'missing'));
  // MDM cards: whichever MDM actually has the device (Meraki, IRU or both
  // during the rollout). A holder mismatch can come from either one.
  const mdmFields = (m, idKey) => ({
    'ID':             m[idKey] || '—',
    'SN':             m.sn ? copyable(m.sn, { label: 'SN', mono: true }) : '—',
    'Model':          m.systemModel || '—',
    'OS':             m.osName || '—',
    'Owner':          m.ownerEmail || '(no owner)',
    'First enrolled': m.firstEnrolledAt ? formatDate(m.firstEnrolledAt) : '—',
    'Last seen':      m.lastSeenAt ? formatDate(m.lastSeenAt) : '—',
  });
  if (device.meraki || kind.id === 'meraki_orphan') {
    cells.push(renderSourceCard('Meraki MDM', device.meraki ? mdmFields(device.meraki, 'merakiId') : null, device.meraki ? 'present' : 'missing'));
  }
  if (device.iru || kind.id === 'iru_orphan') {
    cells.push(renderSourceCard('IRU MDM', device.iru ? mdmFields(device.iru, 'iruId') : null, device.iru ? 'present' : 'missing'));
  }
  if (kind.id.startsWith('testing')) {
    cells.push(renderSourceCard('Testing devices sheet', device.testing ? {
      'Sheet tab':   device.testing.sheetTab || '—',
      'Device tag':  device.testing.deviceTag || '—',
      'Model':       device.testing.model || '—',
      'SN':          device.testing.sn ? copyable(device.testing.sn, { label: 'SN', mono: true }) : '—',
      'IMEI':        device.testing.imei || '—',
      'Used by':     device.testing.usedByName || device.testing.usedByEmail || '(none)',
      'Date borrowed': device.testing.dateBorrowed || '—',
    } : null, device.testing ? 'present' : 'missing'));
  }
  return el('div', {
    style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px',
  }, ...cells);
}

function renderSourceCard(title, fields, status) {
  const head = el('div', { class: 'section-head' },
    title,
    status === 'missing'
      ? el('span', { class: 'chip chip-warning' }, 'Missing')
      : el('span', { class: 'chip chip-verified' }, 'Present'),
  );
  if (!fields) {
    return el('section', { class: 'card card-alert' },
      head,
      el('p', { class: 'muted', style: 'margin:0' },
        `No record in ${title}.`),
    );
  }
  return el('section', { class: 'card' },
    head,
    el('table', { class: 'kv' },
      ...Object.entries(fields).map(([k, v]) =>
        el('tr', null,
          el('td', { class: 'k' }, k),
          // Values may be nodes (copyable SNs) as well as strings.
          el('td', { class: 'v' }, v == null || v === '' ? el('span', { class: 'muted' }, '—') : (v instanceof Node ? v : String(v))),
        ),
      ),
    ),
  );
}
